import React from "react";
import { skipToken } from "@reduxjs/toolkit/query/react";
import { Outlet, useParams } from "react-router";
import { Link, Page, PageNotFound, TooltipIcon } from "@sscs/ui";
import { InventoryIcon as PackageIcon, ProjectIcon } from "@sscs/ui/icons";
import { AddToCatalog } from "Components";
import { PERMISSION } from "Constants";
import { useAllowedTo, useCurrentOrganization, useDialog } from "Hooks";
import {
  useGetBaseOrganizationUsageQuery,
  useGetPackageQuery,
  useGetVersionQuery,
} from "Services";
import { isApproved } from "Utils";
import CreateNewButton from "../../components/CreateNewButton/CreateNewButton";
import CreatePackage from "../../components/CreatePackage/CreatePackage";
import { useGetProjectQuery } from "../../services/projects.api";
import styles from "./projectsTab.module.scss";

export type ProjectsOutletContext = {
  onCreatePackage: () => void;
};

type ProjectContentProps = {
  groupId: string | undefined;
  projectId: string | undefined;
  packageId: string | undefined;
};

const ProjectContent = ({
  groupId,
  packageId,
  projectId,
}: ProjectContentProps): React.JSX.Element => {
  const { data: project, isLoading } = useGetProjectQuery(
    projectId
      ? {
          projectId: projectId,
        }
      : skipToken,
  );
  const { data: packageInfo } = useGetPackageQuery(
    packageId
      ? {
          packageId: packageId,
        }
      : skipToken,
  );
  const { data: inCatalogVersion } = useGetVersionQuery(
    packageInfo?.catalog_version_id
      ? { versionId: packageInfo.catalog_version_id }
      : skipToken,
  );
  const currentOrganization = useCurrentOrganization();
  const createDialog = useDialog();
  const { data: licensing } = useGetBaseOrganizationUsageQuery(
    currentOrganization
      ? { organizationId: currentOrganization.id }
      : skipToken,
  );
  const canCreatePackage = useAllowedTo({
    permissions: [PERMISSION.CREATE_PACKAGE],
    groupId,
  });

  if (isLoading) {
    return (
      <Page.Root>
        <Page.Body status={{ status: "LOADING" }} />
      </Page.Root>
    );
  }

  if (!project) {
    return (
      <PageNotFound
        title="We can't find that project"
        byline="Try selecting a project from the sidebar"
      />
    );
  }

  const maxPackagesLimit = licensing?.max_packages_per_organization ?? 0;

  const renderActions = (): React.ReactNode => {
    if (packageId) {
      if (
        packageInfo?.catalog_version_id &&
        inCatalogVersion &&
        canCreatePackage
      ) {
        return (
          <AddToCatalog
            packageId={packageInfo.package_id}
            packageName={packageInfo.name}
            inCatalog={packageInfo.in_catalog}
            disabled={!isApproved(inCatalogVersion.approval_status)}
          />
        );
      } else return undefined;
    } else if (canCreatePackage) {
      return (
        <CreateNewButton
          id="create-package"
          type="release-package"
          onClick={createDialog.open}
          disabled={project.packages.length >= maxPackagesLimit}
        />
      );
    }

    return undefined;
  };

  return (
    <Page.Root>
      <Page.Header
        title={
          <span className={styles.breadcrumbs}>
            {packageId ? (
              <>
                {/* Layout is rendered on the projects route, by default relative to the route hierarchy */}
                <Link to="." underline="always">
                  <ProjectIcon />
                  {project?.name}
                </Link>
                &nbsp;/&nbsp;
                <PackageIcon size="md" />
                {packageInfo?.name}
              </>
            ) : (
              <>
                <ProjectIcon />
                {project.name}
              </>
            )}
          </span>
        }
        actions={renderActions()}
        description={
          !packageId && project.description ? project.description : undefined
        }
        details={
          !packageId
            ? [
                <React.Fragment key="packages-limit">
                  {`${project.packages.length.toLocaleString()}/${maxPackagesLimit.toLocaleString()} Package${
                    maxPackagesLimit !== 1 ? "s" : ""
                  }`}
                  <TooltipIcon
                    title={`Number of packages is limited to ${maxPackagesLimit.toLocaleString()} across the organization`}
                  />
                </React.Fragment>,
              ]
            : undefined
        }
      />
      <Outlet
        context={
          {
            onCreatePackage: createDialog.open,
          } satisfies ProjectsOutletContext
        }
      />
      {projectId && !packageId && (
        <CreatePackage
          isOpen={createDialog.isOpen}
          onClose={createDialog.close}
          projectId={projectId}
          projectName={project.name}
        />
      )}
    </Page.Root>
  );
};

const ContentLayout = (): React.JSX.Element => {
  const { groupId, packageId, projectId } = useParams();

  return (
    <ProjectContent
      key={`${groupId} ${projectId} ${packageId}`}
      groupId={groupId}
      projectId={projectId}
      packageId={packageId}
    />
  );
};

export default ContentLayout;
